import React, { useState, useEffect } from 'react'
import { BiX, BiChevronLeft, BiChevronRight, BiChart, BiTable, BiCard, BiVideo } from 'react-icons/bi'
import AIInsightCards from './AIInsightCards'
import AIChart from './AIChart'
import ReportDataTable from './ReportDataTable'
import SupportVideos from './SupportVideos'

interface ResultData {
  reportData?: any
  reportType?: string
  videoUrls?: string[]
  startDate?: string
  endDate?: string
  report_id?: string
}

interface AgentResultsPanelProps {
  results: ResultData[]
  currentIndex: number
  onIndexChange: (index: number) => void
  onClose: () => void
}

const AgentResultsPanel: React.FC<AgentResultsPanelProps> = ({
  results,
  currentIndex,
  onIndexChange,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<'insights' | 'chart' | 'table' | 'video'>('insights')

  if (results.length === 0) {
    return null
  }

  const currentResult = results[currentIndex] || results[results.length - 1]
  const hasReportData = !!currentResult?.reportData
  const hasVideoUrls = !!currentResult?.videoUrls?.length

  // Determine which tabs are available
  const availableTabs = []
  if (hasReportData) {
    availableTabs.push('insights', 'chart', 'table')
  }
  if (hasVideoUrls) {
    availableTabs.push('video')
  }

  // Make sure activeTab is valid (useEffect to avoid setState during render)
  useEffect(() => {
    if (!availableTabs.includes(activeTab) && availableTabs.length > 0) {
      setActiveTab(availableTabs[0] as any)
    }
  }, [availableTabs.join(','), activeTab])

  return (
    <div className="tw-flex tw-flex-col tw-h-full tw-bg-white tw-border-l tw-border-gray-200">
      {/* Header */}
      <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-border-b tw-border-gray-200 tw-bg-gray-50">
        <div className="tw-flex tw-items-center tw-gap-2">
          <h3 className="tw-font-semibold tw-text-gray-800 tw-text-sm">Results</h3>
          {results.length > 1 && (
            <div className="tw-flex tw-items-center tw-gap-1 tw-ml-2">
              <button
                type="button"
                onClick={() => onIndexChange(Math.max(0, currentIndex - 1))}
                disabled={currentIndex === 0}
                className="tw-p-1 tw-rounded tw-text-gray-500 hover:tw-bg-gray-200 disabled:tw-opacity-30 disabled:tw-cursor-not-allowed"
              >
                <BiChevronLeft size={18} />
              </button>
              <span className="tw-text-xs tw-text-gray-500">
                {currentIndex + 1} / {results.length}
              </span>
              <button
                type="button"
                onClick={() => onIndexChange(Math.min(results.length - 1, currentIndex + 1))}
                disabled={currentIndex === results.length - 1}
                className="tw-p-1 tw-rounded tw-text-gray-500 hover:tw-bg-gray-200 disabled:tw-opacity-30 disabled:tw-cursor-not-allowed"
              >
                <BiChevronRight size={18} />
              </button>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="tw-p-1.5 tw-rounded tw-text-gray-500 hover:tw-text-gray-700 hover:tw-bg-gray-200 tw-transition-colors"
          title="Close panel"
        >
          <BiX size={20} />
        </button>
      </div>

      {/* Tabs */}
      {availableTabs.length > 1 && (
        <div className="tw-flex tw-border-b tw-border-gray-200 tw-px-2 tw-bg-gray-50">
          {hasReportData && (
            <>
              <button
                type="button"
                onClick={() => setActiveTab('insights')}
                className={`tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-text-sm tw-border-b-2 tw-transition-colors ${
                  activeTab === 'insights'
                    ? 'tw-border-blue-500 tw-text-blue-600'
                    : 'tw-border-transparent tw-text-gray-500 hover:tw-text-gray-700'
                }`}
              >
                <BiCard size={16} />
                <span>Insights</span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('chart')}
                className={`tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-text-sm tw-border-b-2 tw-transition-colors ${
                  activeTab === 'chart'
                    ? 'tw-border-blue-500 tw-text-blue-600'
                    : 'tw-border-transparent tw-text-gray-500 hover:tw-text-gray-700'
                }`}
              >
                <BiChart size={16} />
                <span>Chart</span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('table')}
                className={`tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-text-sm tw-border-b-2 tw-transition-colors ${
                  activeTab === 'table'
                    ? 'tw-border-blue-500 tw-text-blue-600'
                    : 'tw-border-transparent tw-text-gray-500 hover:tw-text-gray-700'
                }`}
              >
                <BiTable size={16} />
                <span>Data</span>
              </button>
            </>
          )}
          {hasVideoUrls && (
            <button
              type="button"
              onClick={() => setActiveTab('video')}
              className={`tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-text-sm tw-border-b-2 tw-transition-colors ${
                activeTab === 'video'
                  ? 'tw-border-blue-500 tw-text-blue-600'
                  : 'tw-border-transparent tw-text-gray-500 hover:tw-text-gray-700'
              }`}
            >
              <BiVideo size={16} />
              <span>Videos</span>
            </button>
          )}
        </div>
      )}

      {/* Content */}
      <div className="tw-flex-1 tw-overflow-y-auto tw-p-4">
        {!hasReportData && !hasVideoUrls ? (
          <div className="tw-flex tw-items-center tw-justify-center tw-h-full tw-text-gray-400 tw-text-sm">
            No data to display
          </div>
        ) : (
          <>
            {activeTab === 'insights' && hasReportData && (
              <AIInsightCards
                reportData={currentResult.reportData}
                reportType={currentResult.reportType}
              />
            )}
            {activeTab === 'chart' && hasReportData && currentResult.reportType && (
              <AIChart
                reportData={currentResult.reportData}
                reportType={currentResult.reportType}
              />
            )}
            {activeTab === 'table' && hasReportData && currentResult.reportType && (
              <ReportDataTable
                reportType={currentResult.reportType}
                maxRows={100}
                reportData={currentResult.reportData}
              />
            )}
            {activeTab === 'video' && hasVideoUrls && (
              <SupportVideos videoUrls={currentResult.videoUrls} />
            )}
          </>
        )}
      </div>

      {/* Report type indicator */}
      {currentResult?.reportType && (
        <div className="tw-px-4 tw-py-2 tw-border-t tw-border-gray-200 tw-bg-gray-50">
          <div className="tw-flex tw-items-center tw-justify-between">
            <span className="tw-text-xs tw-text-gray-500">
              Report: <span className="tw-font-medium tw-text-gray-700">{currentResult.reportType}</span>
            </span>
            {currentResult.startDate && currentResult.endDate && (
              <span className="tw-text-xs tw-text-gray-400">
                {currentResult.startDate} - {currentResult.endDate}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default AgentResultsPanel
